import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import { iRootDispatch, iRootState } from '../../store';
import {
  FaShieldAlt,
  FaShareAlt,
  FaChevronDown,
  FaPlus,
  FaTrash,
  FaList
} from 'react-icons/fa';
import logorv from '../../resources/imgs/logo-lado-bom-seguros.svg';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Input from '../../components/Input';
import { FieldLabel } from '../../styles';
import VMasker from 'vanilla-masker';
import { AddressService } from '../../services/address';
import {
  maskZipCode,
  maskCpfOrCnpj,
  maskDate,
  maskPhoneNumber
} from '../../services/masks';
import { getEstates } from '../../services/broker';
import {
  createPreAnalysis,
  PreAnalysisResponse,
  shareViaWhatsApp
} from '../../services/bond-insurance-pre-analysis';
import { hirePropertyTypeOptions } from '../bond-insurance-budget-form/types';
import { cleanUserSession } from '../../services/auth';
import { cpfValidity } from '../../util';
import useDocumentLookup from '../pwa-new-analysis/hooks/useDocumentLookup';

// --- Styled Components ---

const Container = styled.div`
  min-height: 100vh;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const Header = styled.div`
  width: 100%;
  height: 64px;
  background-color: #ffffff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
`;

const HeaderLogo = styled.img`
  height: 32px;
`;

const LogoutContext = styled.button`
  background: none;
  border: none;
  color: #aaadb1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  cursor: pointer;
  svg {
    fill: #aaadb1;
    width: 20px;
    height: 20px;
  }
`;

const Content = styled.div`
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex: 1;
`;

const SubHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AnalysisTitle = styled.h2`
  font-weight: 600;
  font-size: 20px;
  color: #a3a3a3;
  margin: 0;
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const IconWrapper = styled.div`
  color: #59249b;
  font-size: 24px;
  display: flex;
  align-items: center;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const CardTitle = styled.span`
  font-weight: 700;
  font-size: 26px;
  color: #393939;
`;

const Description = styled.p`
  font-weight: 400;
  font-size: 14px;
  line-height: 19px;
  color: #6b6f77;
  margin: 0;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

const SectionTitle = styled.h3`
  font-weight: 600;
  font-size: 24px;
  color: #854ee0;
  margin: 0;
  white-space: nowrap;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #854ee0;
  width: 100%;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
`;

const RadioButton = styled.div<{ active: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 10px;
  background: ${props => (props.active ? '#59249B' : '#FFFFFF')};
  border: 1px solid ${props => (props.active ? '#59249B' : '#DDDDDD')};
  border-radius: 6px;
  cursor: pointer;
  height: 48px;

  span {
    font-weight: 600;
    font-size: 16px;
    color: ${props => (props.active ? '#FFFFFF' : '#2B313C')};
  }
`;

const CustomRadio = styled.div<{ active: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid ${props => (props.active ? '#AC8AE4' : '#6B7280')};
  background: #ffffff;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #59249b;
    display: ${props => (props.active ? 'block' : 'none')};
  }
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 24px;
`;

const buttonBaseStyles = css`
  flex: 1;
  height: 46px;
  border-radius: 4px;
  border: none;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const BackButton = styled.button`
  ${buttonBaseStyles}
  background: #6B7280;
  color: #ffffff;
`;

const SubmitButton = styled.button`
  ${buttonBaseStyles}
  background: #59249B;
  color: #ffffff;
`;

// Loading Screen Styles
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  gap: 24px;
`;

const LoadingText = styled.h3`
  font-weight: 600;
  font-size: 18px;
  color: #59249b;
`;

const ProgressTrack = styled.div`
  width: 80%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
`;

const progressAnimation = keyframes`
  0% { width: 0%; }
  100% { width: 100%; }
`;

const ProgressBar = styled.div`
  height: 100%;
  background: #59249b;
  animation: ${progressAnimation} 3s linear forwards;
`;

// Result Screen Styles
const ResultCard = styled.div<{ expanded?: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 12px;
  gap: 16px; /* Reduced gap slightly for compact view */
  width: 100%;
  background: #ffffff;
  border: 1px solid #dddddd;
  border-radius: 8px;
  margin-bottom: 12px;
`;

const CardTopRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const InsurerLogo = styled.div<{ color: string }>`
  width: 65px;
  height: 65px;
  background-color: ${props => props.color}; /* Placeholder color or url */
  border-radius: 4px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
  font-size: 24px;
`;

const InsurerInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 7px;
  flex: 1;
`;

const InsurerName = styled.span`
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  color: #393939;
`;

const StatusBadge = styled.div<{ type: 'approved' | 'rejected' }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 4px 12px;
  gap: 8px;
  height: 27px;
  border: 1px solid #dddddd;
  border-radius: 16px;
`;

const StatusDot = styled.div<{ type: 'approved' | 'rejected' }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${props => (props.type === 'approved' ? '#26FF26' : '#FF3B30')};
  border: 1px solid ${props => (props.type === 'approved' ? '#00F52D' : '#FF3B30')};
`;

const StatusText = styled.span`
  font-weight: 600;
  font-size: 14px;
  line-height: 19px;
  color: #393939;
`;

const ExpandIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2b313c;
  cursor: pointer;
`;

const DetailsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
`;

const DetailColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.span`
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: #808080;
`;

const DetailValue = styled.span`
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  color: #393939;
`;

const ActionButtonsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const ValidateButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 12px 16px;
  gap: 10px;
  flex: 1;
  height: 46px;
  background: #34c759;
  border-radius: 4px;
  border: none;
  font-weight: 600;
  font-size: 16px;
  color: #ffffff;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const MyAnalysesButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 12px 16px;
  gap: 10px;
  flex: 1;
  height: 46px;
  background: #59249b;
  border-radius: 4px;
  border: none;
  font-weight: 600;
  font-size: 16px;
  color: #ffffff;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const ShareButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 12px 16px;
  gap: 10px;
  width: 54px;
  height: 46px;
  background: #00a2dd;
  border-radius: 4px;
  border: none;
  color: #ffffff;
  font-size: 20px;
  cursor: pointer;
  &:hover {
    opacity: 0.9;
  }
`;

const FinalBackButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 12px 16px;
  gap: 10px;
  width: 100%;
  height: 46px;
  background: #6b7280;
  border-radius: 4px;
  border: none;
  font-weight: 600;
  font-size: 16px;
  color: #ffffff;
  cursor: pointer;
  margin-top: 12px;
  &:hover {
    opacity: 0.9;
  }
`;

// --- Validation Screen Styles ---

const ValidationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StepHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const StepperContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px;
`;

const StepperVisual = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  justify-content: center;
`;

const StepperDot = styled.div<{ active?: boolean; finished?: boolean }>`
  width: 25px;
  height: 25px;
  box-sizing: border-box;
  background: ${props => (props.active ? '#59249B' : '#FFFFFF')};
  border: 1px solid ${props => (props.active ? '#59249B' : '#C8ACFD')};
  border-radius: 50%;
  z-index: 1;
`;

const StepperLine = styled.div`
  width: 150px;
  height: 1px;
  background: #59249b;
  border: 1px solid #59249b;
`;

const ValidateTitle = styled.div`
  width: 100%;
  padding: 4px 8px;
  border: 1px solid #dddddd;
  border-radius: 8px;
  display: flex;
  align-items: center;

  span {
    font-weight: 600;
    font-size: 20px;
    color: #854ee0;
  }
`;

const ValidationSectionTitle = styled.h4`
  font-weight: 400;
  font-size: 20px;
  color: #6b6f77;
  margin: 0;
  text-transform: uppercase;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
`;

const ToggleInputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid #dddddd;
  border-radius: 4px;
  padding: 0 10px;
  height: 42px;
  background: white;

  &.disabled {
    background: #e4e4e4;
  }
`;

const StyledInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  font-family: 'Open Sans', sans-serif;
  font-size: 16px;
  color: #393939;

  &::placeholder {
    color: rgba(57, 57, 57, 0.3);
  }

  &:disabled {
    background: transparent;
    color: #6b6f77;
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #59249b;

  &:disabled {
    cursor: default;
    color: #999;
  }
`;

const BlockIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 2C5.58 2 2 5.58 2 10C2 14.42 5.58 18 10 18C14.42 18 18 14.42 18 10C18 5.58 14.42 2 10 2ZM10 16C6.69 16 4 13.31 4 10C4 8.56 4.51 7.23 5.37 6.18L13.82 14.63C12.77 15.49 11.44 16 10 16ZM14.63 13.82L6.18 5.37C7.23 4.51 8.56 4 10 4C13.31 4 16 6.69 16 10C16 11.44 15.49 12.77 14.63 13.82Z"
      fill="currentColor"
    />
  </svg>
);

// --- Logic & Component ---

type AnalysisStep = 'FORM' | 'LOADING' | 'RESULT' | 'VALIDATION' | 'PERSONAL_INFO';

interface FormValues {
  document: string; // CPF only
  name: string;
  estateId: string;
  rentValue: string;
}

interface ValidationFormValues {
  // Address
  zipCode: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  propertyType: string;
  realtor: string;

  // Values
  rentValue: string;
}

interface SolidaryTenant {
  cpf: string;
  name: string;
  motherName: string;
  gender: string;
  birthDate: string;
  civilState: string;
  nationality: string;
  email: string;
  cellphone: string;
  isResident: boolean;
  isPoliticallyExposed: 'yes' | 'no' | 'related';
  employmentRelationship: string;
  profession: string;
  monthlyIncome: string;
  otherIncome: string;
}

interface PersonalInfoFormValues {
  // Personal Data
  cpf: string;
  name: string;
  motherName: string;
  gender: string;
  birthDate: string;
  civilState: string;
  nationality: string;
  email: string;
  cellphone: string;

  hasSocialName: boolean;
  socialName: string;

  isPoliticallyExposed: 'yes' | 'no' | 'related';
  pepName: string;
  pepCpf: string;
  pepRelationship: string;

  employmentRelationship: string;
  profession: string;
  monthlyIncome: string;
  otherIncome: string;

  // Multiple Solidary Tenants
  solidaryTenants: SolidaryTenant[];
}

const emptySolidaryTenant: SolidaryTenant = {
  cpf: '',
  name: '',
  motherName: '',
  gender: '',
  birthDate: '',
  civilState: '',
  nationality: 'Brasileira',
  email: '',
  cellphone: '',
  isResident: true,
  isPoliticallyExposed: 'no',
  employmentRelationship: '',
  profession: '',
  monthlyIncome: '',
  otherIncome: ''
};

const mapState = (state: iRootState) => ({
  user: state.user
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  // actions...
});

type Props = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch> &
  RouteComponentProps;

const PwaBondInsuranceAnalysis: React.FC<Props> = props => {
  const { user } = props;
  const [step, setStep] = useState<AnalysisStep>('FORM');
  const [quotationData, setQuotationData] = useState<FormValues | null>(null);
  const [estates, setEstates] = useState<{ id: string; name: string }[]>([]);
  const [preAnalysisResult, setPreAnalysisResult] = useState<PreAnalysisResponse | null>(
    null
  );
  const [loadingEstates, setLoadingEstates] = useState(true);
  const [isUserEstate, setIsUserEstate] = useState(false);
  const { lookup: lookupDocument, isLoading: isDocumentLookupLoading } = useDocumentLookup();

  const formatBirthDateForMask = (birthDate: string) => {
    if (!birthDate) return '';
    if (birthDate.includes('/')) return birthDate;

    const [year, month, day] = birthDate.split('T')[0].split('-');
    if (year && month && day) {
      return maskDate(`${day.padStart(2, '0')}${month.padStart(2, '0')}${year}`);
    }

    return birthDate;
  };

  const mapGenderFromLookup = (gender?: string) => {
    if (!gender) return '';
    const upper = gender.toUpperCase();
    if (upper.startsWith('M')) return 'M';
    if (upper.startsWith('F')) return 'F';
    return 'O';
  };

  const applyCpfLookupToFields = async (
    cpf: string,
    setFieldValue: (field: string, value: any) => void,
    options?: { includeExtendedFields?: boolean },
  ) => {
    const cleanCpf = cpf.replace(/\D/g, '');
    if (cleanCpf.length !== 11 || !cpfValidity(cpf)) return;

    const result = await lookupDocument(cpf);
    if (!result) return;

    if (result.name) {
      setFieldValue('name', result.name);
    }

    if (options?.includeExtendedFields) {
      if (result.motherName) {
        setFieldValue('motherName', result.motherName);
      }
      if (result.gender) {
        setFieldValue('gender', mapGenderFromLookup(result.gender));
      }
      if (result.birthDate) {
        setFieldValue('birthDate', formatBirthDateForMask(result.birthDate));
      }
    }
  };

  // Load estates on mount
  useEffect(() => {
    const loadEstates = async () => {
      try {
        // Check if user is from an estate (imobiliária)
        if (user.estate?.id) {
          // User is from an estate - use their estate directly
          setEstates([
            { id: user.estate.id, name: user.estate.name || 'Minha Imobiliária' }
          ]);
          setIsUserEstate(true);
          setLoadingEstates(false);
          return;
        }

        // User is from a broker - get list of estates
        const brokerId = user.broker?.id;

        if (brokerId) {
          const estatesList = await getEstates(brokerId);
          const estatesListOrdened = estatesList.sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          setEstates(estatesListOrdened.map(e => ({ id: e.id, name: e.name })));
        } else {
          console.warn('User has no broker or estate associated');
        }
      } catch (error) {
        console.error('Error loading estates:', error);
      } finally {
        setLoadingEstates(false);
      }
    };

    loadEstates();
  }, [user]);

  // --- Render Steps ---

  const renderForm = () => (
    <>
      <SubHeader>
        <AnalysisTitle>Nova análise</AnalysisTitle>
        <CardHeader>
          <IconWrapper>
            <FaShieldAlt />
          </IconWrapper>
          <CardTitle>Seguro fiança</CardTitle>
        </CardHeader>
        <Description>
          Realize uma análise de Seguro Fiança em poucos segundos! Após a pré-análise você
          pode concluir a cotação com os dados restantes para realizar a contratação via
          painel.
        </Description>
      </SubHeader>

      <SectionHeader>
        <SectionTitle>Dados do inquilino</SectionTitle>
        <Divider />
      </SectionHeader>

      <Formik
        enableReinitialize
        initialValues={
          {
            document: '',
            name: '',
            estateId: estates.length === 1 ? estates[0].id : '',
            rentValue: ''
          } as FormValues
        }
        validationSchema={Yup.object({
          document: Yup.string()
            .required('Campo obrigatório')
            .min(14, 'CPF inválido')
            .test('cof-valido', 'CPF inválido', value => cpfValidity(value || '')),
          name: Yup.string().required('Campo obrigatório'),
          estateId: Yup.string().required('Selecione uma imobiliária'),
          rentValue: Yup.string().required('Campo obrigatório')
        })}
        onSubmit={async (values, { setSubmitting }) => {
          console.log('Starting simulation with:', values);
          setQuotationData(values);
          setStep('LOADING');

          try {
            // Parse money values to numbers
            const parseMoneyValue = (val: string) => {
              if (!val) return 0;
              return parseFloat(val.replace(/[R$\s.]/g, '').replace(',', '.')) || 0;
            };

            const result = await createPreAnalysis({
              cpf: values.document.replace(/\D/g, ''),
              name: values.name,
              rentValue: parseMoneyValue(values.rentValue),
              estateId: values.estateId
            });

            setPreAnalysisResult(result);
            setStep('RESULT');
          } catch (error) {
            console.error('Pre-analysis error:', error);
            alert('Erro ao realizar pré-análise. Tente novamente.');
            setStep('FORM');
          } finally {
            setSubmitting(false);
          }
        }}
        render={({
          values,
          setFieldValue,
          touched,
          errors,
          handleSubmit,
          isSubmitting
        }) => (
          <form onSubmit={handleSubmit}>
            <FormSection>
              <div>
                <FieldLabel required>Tipo de cliente</FieldLabel>
                <RadioGroup>
                  <RadioButton active={true}>
                    <CustomRadio active={true} />
                    <span>Pessoa física</span>
                  </RadioButton>
                  <RadioButton
                    active={false}
                    style={{ opacity: 0.5, cursor: 'not-allowed' }}
                    onClick={(e: React.MouseEvent) => e.preventDefault()}
                  >
                    <CustomRadio active={false} />
                    <span>Pessoa jurídica</span>
                  </RadioButton>
                </RadioGroup>
              </div>

              <div>
                <FieldLabel required>CPF</FieldLabel>
                <Input
                  name="document"
                  value={values.document}
                  onChange={async (e: any) => {
                    const rawValue = e.target.value.replace(/\D/g, '');
                    const maskedValue = VMasker.toPattern(rawValue, '999.999.999-99');
                    setFieldValue('document', maskedValue);
                    await applyCpfLookupToFields(maskedValue, setFieldValue);
                  }}
                  placeholder="Digite o CPF"
                  fluid
                  disabled={isDocumentLookupLoading}
                />
                {isDocumentLookupLoading ? (
                  <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                    Consultando CPF...
                  </div>
                ) : null}
                {touched.document && errors.document ? (
                  <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                    {errors.document}
                  </div>
                ) : null}
              </div>

              <div>
                <FieldLabel required>Nome completo</FieldLabel>
                <Input
                  name="name"
                  value={values.name}
                  onChange={(e: any) => setFieldValue('name', e.target.value)}
                  placeholder="Digite seu nome"
                  fluid
                />
                {touched.name && errors.name ? (
                  <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                    {errors.name}
                  </div>
                ) : null}
              </div>

              <div>
                <FieldLabel required>Imobiliária</FieldLabel>
                <div style={{ position: 'relative' }}>
                  <select
                    value={values.estateId}
                    onChange={e => setFieldValue('estateId', e.target.value)}
                    disabled={estates.length === 1}
                    style={{
                      width: '100%',
                      height: 42,
                      borderRadius: 4,
                      borderColor: '#DDDDDD',
                      padding: '10px',
                      appearance: 'none',
                      background: estates.length === 1 ? '#f5f5f5' : 'white',
                      color: estates.length === 1 ? '#666' : '#333'
                    }}
                  >
                    {estates.length !== 1 && (
                      <option value="">Selecione uma imobiliária</option>
                    )}
                    {estates.map(estate => (
                      <option key={estate.id} value={estate.id}>
                        {estate.name}
                      </option>
                    ))}
                  </select>
                  <FaChevronDown
                    style={{
                      position: 'absolute',
                      right: 10,
                      top: 12,
                      pointerEvents: 'none',
                      color: '#666'
                    }}
                  />
                </div>
                {touched.estateId && errors.estateId ? (
                  <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                    {errors.estateId}
                  </div>
                ) : null}
              </div>

              <div>
                <FieldLabel required>Valor do aluguel + encargos</FieldLabel>
                <Input
                  name="rentValue"
                  value={values.rentValue}
                  onChange={(e: any) => {
                    const rawValue = e.target.value;
                    const maskedValue = VMasker.toMoney(rawValue, {
                      precision: 2,
                      separator: ',',
                      delimiter: '.',
                      unit: 'R$ '
                    });
                    setFieldValue('rentValue', maskedValue);
                  }}
                  placeholder="R$ 0,00"
                  fluid
                />
                {touched.rentValue && errors.rentValue ? (
                  <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                    {errors.rentValue}
                  </div>
                ) : null}
              </div>
            </FormSection>

            <FooterButtons>
              <BackButton type="button" onClick={() => props.history.push('/pwa/home')}>
                Voltar
              </BackButton>
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Analisando...' : 'Simular agora'}
              </SubmitButton>
            </FooterButtons>
          </form>
        )}
      />
    </>
  );

  const renderLoading = () => (
    <LoadingContainer>
      <LoadingText>Realizando análise...</LoadingText>
      <ProgressTrack>
        <ProgressBar />
      </ProgressTrack>
    </LoadingContainer>
  );

  const renderResult = () => {
    const isApproved = preAnalysisResult?.status === 'approved';
    const statusType = isApproved ? 'approved' : 'rejected';
    const statusText = isApproved ? 'Pré-Aprovado' : 'Recusado';

    const formatCurrency = (value: number) => {
      return Number(value).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    };

    return (
      <>
        <SubHeader>
          <AnalysisTitle>Nova análise</AnalysisTitle>
          <CardHeader>
            <IconWrapper>
              <FaShieldAlt />
            </IconWrapper>
            <CardTitle>Seguro fiança</CardTitle>
          </CardHeader>
        </SubHeader>

        <SectionHeader>
          <SectionTitle>Pré-análise</SectionTitle>
          <Divider />
        </SectionHeader>

        {/* POTTENCIAL CARD */}
        <ResultCard expanded>
          <CardTopRow>
            <InsurerLogo color="#FF6600">P</InsurerLogo>
            <InsurerInfo>
              <InsurerName>Pottencial Seguradora</InsurerName>
            </InsurerInfo>
            <StatusBadge type={statusType}>
              <StatusDot type={statusType} />
              <StatusText>{statusText}</StatusText>
            </StatusBadge>
            <ExpandIcon>
              <FaChevronDown style={{ transform: 'rotate(180deg)' }} />
            </ExpandIcon>
          </CardTopRow>

          <DetailsRow>
            <DetailColumn>
              <DetailLabel>Nome</DetailLabel>
              <DetailValue>{preAnalysisResult?.name || '-'}</DetailValue>
            </DetailColumn>
            <DetailColumn>
              <DetailLabel>CPF</DetailLabel>
              <DetailValue>{maskCpfOrCnpj(preAnalysisResult?.cpf || '')}</DetailValue>
            </DetailColumn>
            <DetailColumn>
              <DetailLabel>Aluguel + encargos</DetailLabel>
              <DetailValue>
                R$ {formatCurrency(preAnalysisResult?.rentValue || 0)}
              </DetailValue>
            </DetailColumn>
            <DetailColumn>
              <DetailLabel>Total aprovado</DetailLabel>
              <DetailValue>
                R$ {formatCurrency(preAnalysisResult?.approvedLimit || 0)}
              </DetailValue>
            </DetailColumn>
          </DetailsRow>

          <ActionButtonsRow>
            {/* {isApproved && (
              <ValidateButton onClick={() => setStep('VALIDATION')}>
                Validar análise <span style={{ fontSize: '0.8em', marginLeft: 4 }}>(?)</span>
              </ValidateButton>
            )} */}

            <MyAnalysesButton onClick={() => props.history.push('/pwa/pre-analyses')}>
              <FaList /> Minhas pré-análises
            </MyAnalysesButton>

            {isApproved && preAnalysisResult && (
              <ShareButton onClick={() => shareViaWhatsApp(preAnalysisResult)}>
                <FaShareAlt />
              </ShareButton>
            )}
          </ActionButtonsRow>
        </ResultCard>

        <FinalBackButton onClick={() => setStep('FORM')}>Voltar</FinalBackButton>
      </>
    );
  };

  const renderPersonalInfo = () => (
    <>
      <SubHeader>
        <AnalysisTitle>Nova análise</AnalysisTitle>
        <CardHeader>
          <IconWrapper>
            <FaShieldAlt />
          </IconWrapper>
          <CardTitle>Seguro fiança</CardTitle>
        </CardHeader>
      </SubHeader>

      <ValidationContainer>
        <ValidateTitle>
          <span style={{ width: 24, display: 'flex' }}>
            <FaShieldAlt size={16} />
          </span>
          <span>Validar análise</span>
        </ValidateTitle>

        <StepperContainer>
          <StepperVisual>
            <StepperDot finished />
            <StepperLine />
            <StepperDot active />
            <StepperLine style={{ background: '#C8ACFD', border: '1px solid #C8ACFD' }} />
            <StepperDot />
          </StepperVisual>
        </StepperContainer>

        <Formik
          initialValues={
            {
              cpf: quotationData?.document || '',
              name: quotationData?.name || '',
              motherName: '',
              gender: '',
              birthDate: '',
              civilState: '',
              nationality: 'Brasileira',
              email: '',
              cellphone: '',
              hasSocialName: false,
              socialName: '',
              isPoliticallyExposed: 'no',
              pepName: '',
              pepCpf: '',
              pepRelationship: '',
              employmentRelationship: '',
              profession: '',
              monthlyIncome: '',
              otherIncome: '',
              solidaryTenants: []
            } as PersonalInfoFormValues
          }
          validationSchema={Yup.object({
            cpf: Yup.string().required('Campo obrigatório'),
            name: Yup.string().required('Campo obrigatório')
            // Add more validation as needed
          })}
          onSubmit={values => {
            console.log('Personal Info Submit:', values);
            alert('Salvar dados e finalizar!');
          }}
          render={({
            values,
            setFieldValue,
            touched,
            errors,
            handleSubmit,
            isSubmitting
          }) => {
            const handleMoney = (field: string, value: string) => {
              setFieldValue(
                field,
                VMasker.toMoney(value, {
                  precision: 2,
                  separator: ',',
                  delimiter: '.',
                  unit: 'R$ '
                })
              );
            };

            return (
              <form onSubmit={handleSubmit}>
                <FormSection>
                  {/* INFORMAÇÕES PESSOAIS */}
                  <ValidationSectionTitle>INFORMAÇÕES PESSOAIS</ValidationSectionTitle>

                  <InputGroup>
                    <FieldLabel required>CPF</FieldLabel>
                    <Input
                      name="cpf"
                      value={values.cpf}
                      onChange={async (e: any) => {
                        const maskedCpf = maskCpfOrCnpj(e.target.value);
                        setFieldValue('cpf', maskedCpf);
                        await applyCpfLookupToFields(maskedCpf, setFieldValue, {
                          includeExtendedFields: true,
                        });
                      }}
                      placeholder="Digite"
                      fluid
                      disabled={isDocumentLookupLoading}
                    />
                    {isDocumentLookupLoading ? (
                      <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                        Consultando CPF...
                      </div>
                    ) : null}
                  </InputGroup>

                  <InputGroup>
                    <FieldLabel required>Nome do locatário</FieldLabel>
                    <Input
                      name="name"
                      value={values.name}
                      onChange={(e: any) => setFieldValue('name', e.target.value)}
                      placeholder="Digite"
                      fluid
                    />
                  </InputGroup>

                  <InputGroup>
                    <FieldLabel required>Nome da mãe</FieldLabel>
                    <Input
                      name="motherName"
                      value={values.motherName}
                      onChange={(e: any) => setFieldValue('motherName', e.target.value)}
                      placeholder="Digite"
                      fluid
                    />
                  </InputGroup>

                  <Row>
                    <InputGroup style={{ flex: 1 }}>
                      <FieldLabel required>Gênero</FieldLabel>
                      <div style={{ position: 'relative' }}>
                        <select
                          value={values.gender}
                          onChange={e => setFieldValue('gender', e.target.value)}
                          style={{
                            width: '100%',
                            height: 42,
                            borderRadius: 4,
                            borderColor: '#DDDDDD',
                            padding: '10px',
                            appearance: 'none',
                            background: 'white'
                          }}
                        >
                          <option value="">Selecione</option>
                          <option value="M">Masculino</option>
                          <option value="F">Feminino</option>
                          <option value="O">Outro</option>
                        </select>
                        <FaChevronDown
                          style={{
                            position: 'absolute',
                            right: 10,
                            top: 12,
                            pointerEvents: 'none',
                            color: '#666'
                          }}
                        />
                      </div>
                    </InputGroup>

                    <InputGroup style={{ flex: 1 }}>
                      <FieldLabel required>Data de nascimento</FieldLabel>
                      <Input
                        name="birthDate"
                        value={values.birthDate}
                        onChange={(e: any) =>
                          setFieldValue('birthDate', maskDate(e.target.value))
                        }
                        placeholder="Digite"
                        fluid
                      />
                    </InputGroup>
                  </Row>

                  <Row>
                    <InputGroup style={{ flex: 1 }}>
                      <FieldLabel required>Estado civil</FieldLabel>
                      <div style={{ position: 'relative' }}>
                        <select
                          value={values.civilState}
                          onChange={e => setFieldValue('civilState', e.target.value)}
                          style={{
                            width: '100%',
                            height: 42,
                            borderRadius: 4,
                            borderColor: '#DDDDDD',
                            padding: '10px',
                            appearance: 'none',
                            background: 'white'
                          }}
                        >
                          <option value="">Selecione</option>
                          <option value="Solteiro">Solteiro</option>
                          <option value="Casado">Casado</option>
                          <option value="Divorciado">Divorciado</option>
                          <option value="Viuvo">Viúvo</option>
                        </select>
                        <FaChevronDown
                          style={{
                            position: 'absolute',
                            right: 10,
                            top: 12,
                            pointerEvents: 'none',
                            color: '#666'
                          }}
                        />
                      </div>
                    </InputGroup>

                    <InputGroup style={{ flex: 1 }}>
                      <FieldLabel required>Nacionalidade</FieldLabel>
                      <Input
                        name="nationality"
                        value={values.nationality}
                        onChange={(e: any) =>
                          setFieldValue('nationality', e.target.value)
                        }
                        placeholder="Digite"
                        fluid
                      />
                    </InputGroup>
                  </Row>

                  <Row>
                    <InputGroup style={{ flex: 1 }}>
                      <FieldLabel required>E-mail</FieldLabel>
                      <Input
                        name="email"
                        value={values.email}
                        onChange={(e: any) => setFieldValue('email', e.target.value)}
                        placeholder="Digite"
                        fluid
                      />
                    </InputGroup>

                    <InputGroup style={{ flex: 1 }}>
                      <FieldLabel required>Celular</FieldLabel>
                      <Input
                        name="cellphone"
                        value={values.cellphone}
                        onChange={(e: any) =>
                          setFieldValue('cellphone', maskPhoneNumber(e.target.value))
                        }
                        placeholder="Digite"
                        fluid
                      />
                    </InputGroup>
                  </Row>

                  {/* Nome Social Toggle */}
                  <InputGroup>
                    <FieldLabel required>Incluir nome social</FieldLabel>
                    <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          cursor: 'pointer'
                        }}
                        onClick={() => setFieldValue('hasSocialName', true)}
                      >
                        <CustomRadio active={values.hasSocialName} /> <span>Sim</span>
                      </label>
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          cursor: 'pointer'
                        }}
                        onClick={() => setFieldValue('hasSocialName', false)}
                      >
                        <CustomRadio active={!values.hasSocialName} /> <span>Não</span>
                      </label>
                    </div>
                  </InputGroup>
                  {values.hasSocialName && (
                    <InputGroup>
                      <FieldLabel required>Nome social</FieldLabel>
                      <Input
                        name="socialName"
                        value={values.socialName}
                        onChange={(e: any) => setFieldValue('socialName', e.target.value)}
                        placeholder="Digite"
                        fluid
                      />
                    </InputGroup>
                  )}

                  {/* PEP Toggle */}
                  <InputGroup>
                    <FieldLabel required>Pessoa politicamente exposta?</FieldLabel>
                    <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          cursor: 'pointer'
                        }}
                        onClick={() => setFieldValue('isPoliticallyExposed', 'yes')}
                      >
                        <CustomRadio active={values.isPoliticallyExposed === 'yes'} />{' '}
                        <span>Sim</span>
                      </label>
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          cursor: 'pointer'
                        }}
                        onClick={() => setFieldValue('isPoliticallyExposed', 'no')}
                      >
                        <CustomRadio active={values.isPoliticallyExposed === 'no'} />{' '}
                        <span>Não</span>
                      </label>
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          cursor: 'pointer'
                        }}
                        onClick={() => setFieldValue('isPoliticallyExposed', 'related')}
                      >
                        <CustomRadio active={values.isPoliticallyExposed === 'related'} />{' '}
                        <span>Próximo</span>
                      </label>
                    </div>
                  </InputGroup>
                  {(values.isPoliticallyExposed === 'yes' ||
                    values.isPoliticallyExposed === 'related') && (
                      <>
                        <Row>
                          <InputGroup style={{ flex: 1 }}>
                            <FieldLabel required>Nome da pessoa</FieldLabel>
                            <Input
                              name="pepName"
                              value={values.pepName}
                              onChange={(e: any) =>
                                setFieldValue('pepName', e.target.value)
                              }
                              placeholder="Digite"
                              fluid
                            />
                          </InputGroup>
                          <InputGroup style={{ flex: 1 }}>
                            <FieldLabel required>CPF da pessoa</FieldLabel>
                            <Input
                              name="pepCpf"
                              value={values.pepCpf}
                              onChange={(e: any) =>
                                setFieldValue('pepCpf', maskCpfOrCnpj(e.target.value))
                              }
                              placeholder="Digite"
                              fluid
                            />
                          </InputGroup>
                        </Row>
                        <InputGroup>
                          <FieldLabel required>Grau de parentesco</FieldLabel>
                          <Input
                            name="pepRelationship"
                            value={values.pepRelationship}
                            onChange={(e: any) =>
                              setFieldValue('pepRelationship', e.target.value)
                            }
                            placeholder="Digite"
                            fluid
                          />
                        </InputGroup>
                      </>
                    )}

                  <ValidationSectionTitle style={{ marginTop: 16 }}>
                    INFORMAÇÕES PESSOAIS
                  </ValidationSectionTitle>

                  <Row>
                    <InputGroup style={{ flex: 1 }}>
                      <FieldLabel required>Vínculo empregatício</FieldLabel>
                      <div style={{ position: 'relative' }}>
                        <select
                          value={values.employmentRelationship}
                          onChange={e =>
                            setFieldValue('employmentRelationship', e.target.value)
                          }
                          style={{
                            width: '100%',
                            height: 42,
                            borderRadius: 4,
                            borderColor: '#DDDDDD',
                            padding: '10px',
                            appearance: 'none',
                            background: 'white'
                          }}
                        >
                          <option value="">Selecione</option>
                          <option value="CLT">CLT</option>
                          <option value="Autonomo">Autônomo</option>
                          <option value="Empresario">Empresário</option>
                        </select>
                        <FaChevronDown
                          style={{
                            position: 'absolute',
                            right: 10,
                            top: 12,
                            pointerEvents: 'none',
                            color: '#666'
                          }}
                        />
                      </div>
                    </InputGroup>
                    <InputGroup style={{ flex: 1 }}>
                      <FieldLabel required>Profissão</FieldLabel>
                      <Input
                        name="profession"
                        value={values.profession}
                        onChange={(e: any) => setFieldValue('profession', e.target.value)}
                        placeholder="Digite"
                        fluid
                      />
                    </InputGroup>
                  </Row>

                  <Row>
                    <InputGroup style={{ flex: 1 }}>
                      <FieldLabel required>Renda mensal</FieldLabel>
                      <Input
                        name="monthlyIncome"
                        value={values.monthlyIncome}
                        onChange={(e: any) =>
                          handleMoney('monthlyIncome', e.target.value)
                        }
                        placeholder="Digite"
                        fluid
                      />
                    </InputGroup>
                    <InputGroup style={{ flex: 1 }}>
                      <FieldLabel required>Outras rendas</FieldLabel>
                      <Input
                        name="otherIncome"
                        value={values.otherIncome}
                        onChange={(e: any) => handleMoney('otherIncome', e.target.value)}
                        placeholder="Digite"
                        fluid
                      />
                    </InputGroup>
                  </Row>

                  {/* Solidary Tenants Section */}
                  <ValidationSectionTitle
                    style={{
                      marginTop: 16,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span>Locatários solidários</span>
                    <button
                      type="button"
                      onClick={() =>
                        setFieldValue('solidaryTenants', [
                          ...values.solidaryTenants,
                          { ...emptySolidaryTenant }
                        ])
                      }
                      style={{
                        background: '#59249B',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 14
                      }}
                    >
                      <FaPlus size={12} /> Adicionar
                    </button>
                  </ValidationSectionTitle>

                  {values.solidaryTenants.length === 0 && (
                    <p style={{ color: '#666', fontStyle: 'italic', fontSize: 14 }}>
                      Nenhum locatário solidário adicionado. Clique em
                      &quote;Adicionar&quote; para incluir.
                    </p>
                  )}

                  {values.solidaryTenants.map((tenant, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16,
                        borderLeft: '2px solid #59249B',
                        paddingLeft: 16,
                        marginLeft: 8,
                        marginBottom: 24,
                        paddingTop: 8
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <strong style={{ color: '#59249B' }}>
                          Locatário solidário #{index + 1}
                        </strong>
                        <button
                          type="button"
                          onClick={() => {
                            const newTenants = [...values.solidaryTenants];
                            newTenants.splice(index, 1);
                            setFieldValue('solidaryTenants', newTenants);
                          }}
                          style={{
                            background: '#FF3B30',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            padding: '6px 12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: 12
                          }}
                        >
                          <FaTrash size={10} /> Remover
                        </button>
                      </div>

                      <InputGroup>
                        <FieldLabel required>CPF</FieldLabel>
                        <Input
                          value={tenant.cpf}
                          onChange={(e: any) =>
                            setFieldValue(
                              `solidaryTenants.${index}.cpf`,
                              maskCpfOrCnpj(e.target.value)
                            )
                          }
                          placeholder="Digite"
                          fluid
                        />
                      </InputGroup>
                      <InputGroup>
                        <FieldLabel required>Nome do locatário</FieldLabel>
                        <Input
                          value={tenant.name}
                          onChange={(e: any) =>
                            setFieldValue(`solidaryTenants.${index}.name`, e.target.value)
                          }
                          placeholder="Digite"
                          fluid
                        />
                      </InputGroup>
                      <InputGroup>
                        <FieldLabel required>Nome da mãe</FieldLabel>
                        <Input
                          value={tenant.motherName}
                          onChange={(e: any) =>
                            setFieldValue(
                              `solidaryTenants.${index}.motherName`,
                              e.target.value
                            )
                          }
                          placeholder="Digite"
                          fluid
                        />
                      </InputGroup>
                      <Row>
                        <InputGroup style={{ flex: 1 }}>
                          <FieldLabel required>Gênero</FieldLabel>
                          <div style={{ position: 'relative' }}>
                            <select
                              value={tenant.gender}
                              onChange={e =>
                                setFieldValue(
                                  `solidaryTenants.${index}.gender`,
                                  e.target.value
                                )
                              }
                              style={{
                                width: '100%',
                                height: 42,
                                borderRadius: 4,
                                borderColor: '#DDDDDD',
                                padding: '10px',
                                appearance: 'none',
                                background: 'white'
                              }}
                            >
                              <option value="">Selecione</option>
                              <option value="M">Masculino</option>
                              <option value="F">Feminino</option>
                              <option value="O">Outro</option>
                            </select>
                            <FaChevronDown
                              style={{
                                position: 'absolute',
                                right: 10,
                                top: 12,
                                pointerEvents: 'none',
                                color: '#666'
                              }}
                            />
                          </div>
                        </InputGroup>
                        <InputGroup style={{ flex: 1 }}>
                          <FieldLabel required>Data de nascimento</FieldLabel>
                          <Input
                            value={tenant.birthDate}
                            onChange={(e: any) =>
                              setFieldValue(
                                `solidaryTenants.${index}.birthDate`,
                                maskDate(e.target.value)
                              )
                            }
                            placeholder="Digite"
                            fluid
                          />
                        </InputGroup>
                      </Row>
                      <Row>
                        <InputGroup style={{ flex: 1 }}>
                          <FieldLabel required>E-mail</FieldLabel>
                          <Input
                            value={tenant.email}
                            onChange={(e: any) =>
                              setFieldValue(
                                `solidaryTenants.${index}.email`,
                                e.target.value
                              )
                            }
                            placeholder="Digite"
                            fluid
                          />
                        </InputGroup>
                        <InputGroup style={{ flex: 1 }}>
                          <FieldLabel required>Celular</FieldLabel>
                          <Input
                            value={tenant.cellphone}
                            onChange={(e: any) =>
                              setFieldValue(
                                `solidaryTenants.${index}.cellphone`,
                                maskPhoneNumber(e.target.value)
                              )
                            }
                            placeholder="Digite"
                            fluid
                          />
                        </InputGroup>
                      </Row>
                      <Row>
                        <InputGroup style={{ flex: 1 }}>
                          <FieldLabel required>Profissão</FieldLabel>
                          <Input
                            value={tenant.profession}
                            onChange={(e: any) =>
                              setFieldValue(
                                `solidaryTenants.${index}.profession`,
                                e.target.value
                              )
                            }
                            placeholder="Digite"
                            fluid
                          />
                        </InputGroup>
                        <InputGroup style={{ flex: 1 }}>
                          <FieldLabel required>Renda mensal</FieldLabel>
                          <Input
                            value={tenant.monthlyIncome}
                            onChange={(e: any) =>
                              handleMoney(
                                `solidaryTenants.${index}.monthlyIncome`,
                                e.target.value
                              )
                            }
                            placeholder="R$ 0,00"
                            fluid
                          />
                        </InputGroup>
                      </Row>
                    </div>
                  ))}
                </FormSection>

                <FooterButtons>
                  <BackButton type="button" onClick={() => setStep('VALIDATION')}>
                    Voltar
                  </BackButton>
                  <SubmitButton
                    type="button"
                    className={isSubmitting ? 'loading' : ''}
                    onClick={handleSubmit as any}
                    style={{ background: '#34C759' }}
                  >
                    Salvar
                  </SubmitButton>
                </FooterButtons>
              </form>
            );
          }}
        />
      </ValidationContainer>
    </>
  );

  const renderValidationProperty = () => (
    <>
      <SubHeader>
        <AnalysisTitle>Nova análise</AnalysisTitle>
        <CardHeader>
          <IconWrapper>
            <FaShieldAlt />
          </IconWrapper>
          <CardTitle>Seguro fiança</CardTitle>
        </CardHeader>
      </SubHeader>

      <ValidationContainer>
        <ValidateTitle>
          <span style={{ width: 24, display: 'flex' }}>
            <FaShieldAlt size={16} />
          </span>
          <span>Validar análise</span>
        </ValidateTitle>

        <StepperContainer>
          <StepperVisual>
            <StepperDot active />
            <StepperLine />
            <StepperDot />
          </StepperVisual>
        </StepperContainer>

        <Formik
          initialValues={
            {
              zipCode: '',
              street: '',
              number: '',
              complement: '',
              district: '',
              city: '',
              state: '',
              propertyType: '',
              realtor: estates.find(e => e.id === quotationData?.estateId)?.name || '',

              rentValue: quotationData?.rentValue || ''
            } as ValidationFormValues
          }
          validationSchema={Yup.object({
            zipCode: Yup.string().required('Campo obrigatório'),
            street: Yup.string().required('Campo obrigatório'),
            number: Yup.string().required('Campo obrigatório'),
            city: Yup.string().required('Campo obrigatório'),
            state: Yup.string().required('Campo obrigatório'),
            rentValue: Yup.string().required('Campo obrigatório')
          })}
          onSubmit={values => {
            console.log('Validating analysis with:', values);
            // Handle submission logic
          }}
          render={({
            values,
            setFieldValue,
            touched,
            errors,
            handleSubmit,
            isSubmitting
          }) => {
            const handleCepBlur = async (e: any) => {
              const cep = e.target.value.replace(/\D/g, '');
              if (cep.length === 8) {
                try {
                  // Try to fetch address
                  // Note: userId is optional in service, passing undefined if not available
                  const address = await AddressService.getAddressByZipCode(cep);
                  if (address) {
                    setFieldValue('street', address.street || '');
                    setFieldValue('district', address.district || '');
                    setFieldValue('city', address.city || '');
                    setFieldValue('state', address.state || '');
                  }
                } catch (err) {
                  console.error('Error fetching address', err);
                }
              }
            };

            const handleMoney = (field: string, value: string) => {
              setFieldValue(
                field,
                VMasker.toMoney(value, {
                  precision: 2,
                  separator: ',',
                  delimiter: '.',
                  unit: 'R$ '
                })
              );
            };

            return (
              <form onSubmit={handleSubmit}>
                <FormSection>
                  <ValidationSectionTitle>ENDEREÇO DO IMÓVEL</ValidationSectionTitle>

                  {/* CEP */}
                  <InputGroup>
                    <FieldLabel required>CEP</FieldLabel>
                    <Input
                      name="zipCode"
                      value={values.zipCode}
                      onChange={(e: any) =>
                        setFieldValue('zipCode', maskZipCode(e.target.value))
                      }
                      onBlur={handleCepBlur}
                      placeholder="Digite"
                      fluid
                    />
                    {touched.zipCode && errors.zipCode && (
                      <div style={{ color: 'red', fontSize: '12px' }}>
                        {errors.zipCode}
                      </div>
                    )}
                  </InputGroup>

                  {/* Rua */}
                  <InputGroup>
                    <FieldLabel required>Logradouro</FieldLabel>
                    <Input
                      name="street"
                      value={values.street}
                      onChange={(e: any) => setFieldValue('street', e.target.value)}
                      placeholder="Digite"
                      fluid
                    />
                  </InputGroup>

                  {/* Bairro, Cidade, UF */}
                  <Row>
                    <InputGroup style={{ flex: 1 }}>
                      <FieldLabel required>Bairro</FieldLabel>
                      <Input
                        name="district"
                        value={values.district}
                        onChange={(e: any) => setFieldValue('district', e.target.value)}
                        placeholder="Digite"
                        fluid
                      />
                    </InputGroup>
                    <InputGroup style={{ flex: 1 }}>
                      <FieldLabel required>Cidade</FieldLabel>
                      <Input
                        name="city"
                        value={values.city}
                        onChange={(e: any) => setFieldValue('city', e.target.value)}
                        placeholder="Digite"
                        fluid
                      />
                    </InputGroup>
                    <InputGroup style={{ width: 80 }}>
                      <FieldLabel required>UF</FieldLabel>
                      <div style={{ position: 'relative' }}>
                        <select
                          value={values.state}
                          onChange={e => setFieldValue('state', e.target.value)}
                          style={{
                            width: '100%',
                            height: 42,
                            borderRadius: 4,
                            borderColor: '#DDDDDD',
                            padding: '10px 4px',
                            appearance: 'none',
                            background: 'white'
                          }}
                        >
                          <option value="">UF</option>
                          <option value="AC">AC</option>
                          <option value="AL">AL</option>
                          <option value="AP">AP</option>
                          <option value="AM">AM</option>
                          <option value="BA">BA</option>
                          <option value="CE">CE</option>
                          <option value="DF">DF</option>
                          <option value="ES">ES</option>
                          <option value="GO">GO</option>
                          <option value="MA">MA</option>
                          <option value="MT">MT</option>
                          <option value="MS">MS</option>
                          <option value="MG">MG</option>
                          <option value="PA">PA</option>
                          <option value="PB">PB</option>
                          <option value="PR">PR</option>
                          <option value="PE">PE</option>
                          <option value="PI">PI</option>
                          <option value="RJ">RJ</option>
                          <option value="RN">RN</option>
                          <option value="RS">RS</option>
                          <option value="RO">RO</option>
                          <option value="RR">RR</option>
                          <option value="SC">SC</option>
                          <option value="SP">SP</option>
                          <option value="SE">SE</option>
                          <option value="TO">TO</option>
                        </select>
                      </div>
                    </InputGroup>
                  </Row>

                  {/* Numero, Complemento */}
                  <Row>
                    <InputGroup style={{ width: 100 }}>
                      <FieldLabel required>Número</FieldLabel>
                      <Input
                        name="number"
                        value={values.number}
                        onChange={(e: any) => setFieldValue('number', e.target.value)}
                        placeholder="Digite"
                        fluid
                      />
                    </InputGroup>
                    <InputGroup style={{ flex: 1 }}>
                      <FieldLabel>Complemento</FieldLabel>
                      <Input
                        name="complement"
                        value={values.complement}
                        onChange={(e: any) => setFieldValue('complement', e.target.value)}
                        placeholder="Digite"
                        fluid
                      />
                    </InputGroup>
                  </Row>

                  {/* Tipo Imovel */}
                  <InputGroup>
                    <FieldLabel required>Tipo do imóvel</FieldLabel>
                    <div style={{ position: 'relative' }}>
                      <select
                        value={values.propertyType}
                        onChange={e => setFieldValue('propertyType', e.target.value)}
                        style={{
                          width: '100%',
                          height: 42,
                          borderRadius: 4,
                          borderColor: '#DDDDDD',
                          padding: '10px',
                          appearance: 'none',
                          background: 'white'
                        }}
                      >
                        <option value="">Selecione</option>
                        {hirePropertyTypeOptions.map(option => (
                          <option key={option.key} value={String(option.value)}>
                            {option.text}
                          </option>
                        ))}
                      </select>
                      <FaChevronDown
                        style={{
                          position: 'absolute',
                          right: 10,
                          top: 12,
                          pointerEvents: 'none',
                          color: '#666'
                        }}
                      />
                    </div>
                  </InputGroup>

                  {/* Imobiliaria - Readonly */}
                  <InputGroup>
                    <FieldLabel>Imobiliária</FieldLabel>
                    <Input
                      name="realtor"
                      value={values.realtor}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5' }}
                      fluid
                    />
                  </InputGroup>

                  <ValidationSectionTitle style={{ marginTop: 12 }}>
                    VALORES
                  </ValidationSectionTitle>

                  <InputGroup>
                    <FieldLabel required>Aluguel + encargos</FieldLabel>
                    <Input
                      name="rentValue"
                      value={values.rentValue}
                      onChange={(e: any) => handleMoney('rentValue', e.target.value)}
                      placeholder="Digite"
                      fluid
                    />
                  </InputGroup>
                </FormSection>

                <FooterButtons>
                  <BackButton type="button" onClick={() => setStep('RESULT')}>
                    Voltar
                  </BackButton>
                  <SubmitButton
                    type="button"
                    onClick={() => setStep('PERSONAL_INFO')}
                    style={{ background: '#59249B' }}
                  >
                    Próximo
                  </SubmitButton>
                </FooterButtons>
              </form>
            );
          }}
        />
      </ValidationContainer>
    </>
  );

  return (
    <Container>
      <Header>
        <HeaderLogo src={logorv} alt="Lado Bom Seguros" />
        <LogoutContext onClick={() => cleanUserSession()}>
          {/* Placeholder for Logout Icon */}
          <svg viewBox="0 0 24 24">
            <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
          </svg>
        </LogoutContext>
      </Header>

      <Content>
        {step === 'FORM' && renderForm()}
        {step === 'LOADING' && renderLoading()}
        {step === 'RESULT' && renderResult()}
        {/* {step === 'VALIDATION' && renderValidationProperty()} */}
        {step === 'PERSONAL_INFO' && renderPersonalInfo()}
      </Content>
    </Container>
  );
};

export default connect(mapState, mapDispatch)(PwaBondInsuranceAnalysis);
